import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Heart, MapPin } from "lucide-react";
import axios from "axios";
import { logger } from "@/utils/logger";

const API = process.env.REACT_APP_BACKEND_URL;

const AdoptionCarousel = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    fetchAdoptions();
  }, []);

  const fetchAdoptions = async () => {
    try {
      const res = await axios.get(`${API}/api/browse/adoptions/featured`);
      setListings(res.data.listings || []);
    } catch (err) {
      logger.error("Failed to fetch adoptions:", err);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => 
      prev + 4 >= listings.length ? 0 : prev + 4
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => 
      prev - 4 < 0 ? Math.max(0, listings.length - 4) : prev - 4
    );
  };

  if (loading || listings.length === 0) return null;

  const visibleListings = listings.slice(currentIndex, currentIndex + 4);

  return (
    <section className="py-16 px-6 md:px-12 bg-gradient-to-b from-[#0A0A0A] to-[#050505]">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Heart className="w-5 h-5 text-[#F472B6]" />
              <p className="text-xs tracking-[0.3em] uppercase text-[#F472B6]">Find Your Forever Friend</p>
            </div>
            <h2 className="text-3xl text-[#EDEDED]">Animals Looking for Homes</h2>
            <p className="text-[#A1A1AA] mt-2">Give a loving home to animals in need of rehoming</p>
          </div>
          <div className="flex items-center gap-4">
            <Link 
              to="/adoptions" 
              className="text-sm text-[#F472B6] hover:text-[#F9A8D4] transition-colors"
            >
              View All
            </Link>
            {listings.length > 4 && (
              <div className="flex gap-2">
                <button 
                  onClick={prevSlide}
                  className="p-2 rounded-full border border-[#3F3F46] hover:border-[#F472B6] transition-colors"
                  disabled={currentIndex === 0}
                >
                  <ChevronLeft className="w-5 h-5 text-[#A1A1AA]" />
                </button>
                <button 
                  onClick={nextSlide}
                  className="p-2 rounded-full border border-[#3F3F46] hover:border-[#F472B6] transition-colors"
                  disabled={currentIndex + 4 >= listings.length}
                >
                  <ChevronRight className="w-5 h-5 text-[#A1A1AA]" />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {visibleListings.map((listing) => (
            <AdoptionCard key={listing.listing_id} listing={listing} />
          ))}
        </div>
      </div>
    </section>
  );
};

const AdoptionCard = ({ listing }) => {
  const [imgError, setImgError] = useState(false);
  const fallbackImage = "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400&h=300&fit=crop";
  const imgSrc = imgError ? fallbackImage : (listing.photos?.[0] || fallbackImage);

  return (
    <Link
      to={`/listing/${listing.listing_id}`}
      className="group glass-card overflow-hidden hover:border-[#F472B6]/30 transition-all duration-300 block"
      data-testid={`adoption-${listing.listing_id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#18181B]">
        <img
          src={imgSrc}
          alt={listing.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          onError={() => setImgError(true)}
        />
        <div className="absolute top-2 left-2">
          <span className="px-2 py-1 bg-[#F472B6] text-black text-xs font-medium rounded-full flex items-center gap-1">
            <Heart className="w-3 h-3" /> Adoption
          </span>
        </div>
        {listing.good_with_kids && (
          <div className="absolute top-2 right-2">
            <span className="px-2 py-1 bg-[#22C55E] text-black text-xs font-medium rounded-full">
              Kid-Friendly
            </span>
          </div>
        )}
      </div>
      <div className="p-4">
        <h4 className="text-lg text-[#EDEDED] mb-1 truncate">
          {listing.animal_name || listing.title}
        </h4>
        <p className="text-[#F472B6] font-medium mb-2">
          {listing.price > 0 ? `$${listing.price.toLocaleString()} rehoming fee` : "Free to good home"}
        </p>
        <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
          <span>{listing.breed}</span>
          {listing.age && (
            <>
              <span>•</span>
              <span>{listing.age}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1 mt-2 text-xs text-[#71717A]">
          <MapPin className="w-3 h-3" />
          <span>{listing.location_city}, {listing.location_state}</span>
        </div>
        {/* Compatibility badges */}
        <div className="flex flex-wrap gap-1 mt-3">
          {listing.good_with_dogs && (
            <span className="px-2 py-0.5 bg-[#27272A] text-[#A1A1AA] text-xs rounded">Dogs OK</span>
          )}
          {listing.good_with_cats && (
            <span className="px-2 py-0.5 bg-[#27272A] text-[#A1A1AA] text-xs rounded">Cats OK</span>
          )}
          {listing.house_trained && (
            <span className="px-2 py-0.5 bg-[#27272A] text-[#A1A1AA] text-xs rounded">House Trained</span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default AdoptionCarousel;
